/**
 * Daily job revenue — prorates monthly contract amounts to a single calendar day
 * so EOD/dashboard do not show a full month on one visit day.
 */

export interface JobRevenueContractContext {
  billing_frequency?: string | null
  billing_method?: string | null
}

export interface JobRevenueInput {
  quoted_amount?: number | null
  actual_amount?: number | null
  scheduled_date?: string | null
  actual_end_time?: string | null
  billing_type?: string | null
  contract?: JobRevenueContractContext | null
  /** When line items are loaded, per_month units also trigger proration. */
  line_item_unit_types?: string[] | null
}

export interface JobDailyRevenue {
  quoted: number
  actual: number
  prorated: boolean
}

function parseYmd(dateStr: string): { year: number; month: number; day: number } | null {
  const parts = (dateStr || '').split('-').map(Number)
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null
  return { year: parts[0], month: parts[1], day: parts[2] }
}

export function daysInMonthForDate(dateStr: string): number {
  const p = parseYmd(dateStr)
  if (!p) return 30
  return new Date(p.year, p.month, 0).getDate()
}

export function resolveRevenueDate(job: JobRevenueInput): string | null {
  if (job.scheduled_date) return job.scheduled_date
  if (job.actual_end_time) return job.actual_end_time.split('T')[0]
  return null
}

export function needsMonthlyDailyProration(job: JobRevenueInput): boolean {
  if (job.billing_type !== 'contract') return false
  const contract = job.contract
  if (!contract) return false
  if (contract.billing_method === 'actual_usage') return false
  const freq = (contract.billing_frequency || '').toLowerCase()
  if (freq === 'monthly') return true
  if (job.line_item_unit_types?.includes('per_month')) return true
  return false
}

export function monthlyProrateFactor(job: JobRevenueInput): number {
  if (!needsMonthlyDailyProration(job)) return 1
  const dateStr = resolveRevenueDate(job)
  if (!dateStr) return 1
  const dim = daysInMonthForDate(dateStr)
  return dim > 0 ? 1 / dim : 1
}

export function computeJobDailyRevenue(job: JobRevenueInput): JobDailyRevenue {
  const factor = monthlyProrateFactor(job)
  const quotedRaw = Number(job.quoted_amount || 0)
  const actualRaw = Number(job.actual_amount || 0)
  const quoted = quotedRaw * factor
  const actual = (actualRaw > 0 ? actualRaw : quotedRaw) * factor
  return {
    quoted,
    actual,
    prorated: factor !== 1,
  }
}

export function sumJobDailyRevenue(
  jobs: JobRevenueInput[],
  field: 'quoted' | 'actual' = 'actual'
): number {
  return jobs.reduce((sum, job) => sum + computeJobDailyRevenue(job)[field], 0)
}
